import { Breakpoint, ButtonType, Typography } from '@altibox/design-system-component-lib';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { Product } from 'src/api/@generated/backend';

import { CustomizedPortableText } from 'src/components/customized-portable-text';
import { DataLayerErrorWrapper } from 'src/components/data-layer-wrapper/data-layer-error-wrapper';
import { i18n } from 'src/i18n';
import { NavigationService } from 'src/services/navigation-service';
import { selectProductActionCreator } from 'src/state/order/action-creators/select-product-action-creator';
import { useAppDispatch } from 'src/state/thunk-api';
import { DataLayerEcomErrors } from 'src/types/data-layer-event-object';
import { I18nUtils } from 'src/utils/i18n-utils';
import { formatMonthlyPriceShort } from 'src/utils/monthly-price-utils';
import { CardErrorMessage } from 'src/components/card-error-message';

import {
  StyledCard,
  StyledCardContainer,
  StyledContent,
  StyledImage,
  StyledNavigationButton,
  StyledPrice,
  StyledPriceContainer,
  StyledTitle,
} from './product-select-card.styled';

interface Props {
  product: Product;
}

export const ProductSelectCard: FC<Props> = (props) => {
  const {
    product,
    product: { isAvailable, name, shortName, description, mainImage, pricing, purchaseStatus },
  } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const productName = shortName ?? name;

  const handleButtonClick = () => {
    dispatch(selectProductActionCreator(product));
    return navigate(NavigationService.PRODUCT_DETAILS);
  };

  return (
    <StyledCardContainer>
      <StyledCard>
        <StyledContent>
          <StyledImage src={mainImage.url} alt={mainImage.altText} />
          <StyledTitle variant="paragraph1" component="p" maxBreakpoint={Breakpoint.TABLET}>
            {name}
          </StyledTitle>
          <CustomizedPortableText value={description} variablesMap={{ ...pricing }} />
        </StyledContent>
        <StyledPriceContainer>
          <StyledPrice variant="paragraph1" component="p" maxBreakpoint={Breakpoint.TABLET}>
            {formatMonthlyPriceShort(pricing.monthlyPrice, pricing.numberOfMonthlyInstallments)}
          </StyledPrice>
          <Typography variant="uiText3" component="p" maxBreakpoint={Breakpoint.TABLET}>
            {i18n.component.productSelectCard.downPayment[pricing.paymentType]}
          </Typography>
        </StyledPriceContainer>
        <StyledNavigationButton
          onClick={handleButtonClick}
          buttonType={ButtonType.PRIMARY_B}
          disabled={!purchaseStatus.eligibleToPurchase || !isAvailable}
        >
          {I18nUtils.message(i18n.component.productSelectCard.buttonText, { productName })}
        </StyledNavigationButton>
      </StyledCard>
      {purchaseStatus.alreadyOwned && (
        <DataLayerErrorWrapper errorMessage={DataLayerEcomErrors.ALREADY_BOUGHT_ERROR}>
          <CardErrorMessage>{i18n.component.productSelectCard.alreadyOwned}</CardErrorMessage>
        </DataLayerErrorWrapper>
      )}
      {!isAvailable && !purchaseStatus.alreadyOwned && (
        <DataLayerErrorWrapper errorMessage={DataLayerEcomErrors.ALREADY_BOUGHT_ERROR}>
          <CardErrorMessage>
            {I18nUtils.message(i18n.component.productSelectCard.productNotAvailable, { productName })}
          </CardErrorMessage>
        </DataLayerErrorWrapper>
      )}
    </StyledCardContainer>
  );
};
